import { showLogger } from 'constant/env';
import { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getKeyFromLocalStorage } from 'utils/constant';

type ClusterDbSocketContextType<T = unknown> = {
  socket: Socket | null;
  eventId: T;
  reconnect: () => void;
  isConnected: boolean;
};

export const ClusterDbSocketContext = createContext<
  ClusterDbSocketContextType | undefined
>(undefined);

type ProviderProps = {
  children: ReactNode;
  url?: string;
};

/**
 * Connects to the face-ids namespace and listens for `create` events.
 *
 * Note: Browsers cannot set custom headers during the WebSocket handshake.
 * We pass credentials via `auth` which the server can read at `socket.handshake.auth`.
 * The server middleware currently reads headers (x-app-db, x-app-db-key). To support browsers,
 * ensure the middleware also checks `socket.handshake.auth` or query params.
 */
export function useClusterDbSocket({ children, url }: ProviderProps) {
  const socketRef = useRef<Socket | null>(null);
  const [eventId, setEventId] = useState<unknown>();
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const [isConnected, setIsConnected] = useState(false);

  const reconnect = () => {
    setReconnectTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    // Read encrypted keys from localStorage
    const dbId = getKeyFromLocalStorage('dbId');
    const dbKey = getKeyFromLocalStorage('dbKey');

    if (!dbId || !dbKey) {
      if (showLogger) {
        // eslint-disable-next-line no-console
        console.warn('[socket] Missing dbId/dbKey in storage; skip connecting');
      }
      return;
    }

    const wsUrl =
      url ||
      process.env.REACT_APP_WS_FACE_IDS_URL ||
      'ws://13.231.30.254:3002/face-ids';
    if (showLogger) {
      // eslint-disable-next-line no-console
      console.info('[socket] preparing connect', {
        wsUrl,
        hasDbId: Boolean(dbId),
        hasDbKey: Boolean(dbKey),
      });
    }

    // Important: extraHeaders are not supported in browser. Use `auth` instead.
    const socket = io(wsUrl, {
      transports: ['polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      forceNew: false,
      transportOptions: {
        polling: {
          extraHeaders: {
            // Server cannot read these in browser; here for completeness
            'x-app-db': dbId,
            'x-app-db-key': dbKey,
          },
        },
      },
      autoConnect: false,
    });
    if (!socket.connected) {
      socket.connect();
    }
    socket.on('connect', () => {
      if (showLogger) {
        // eslint-disable-next-line no-console
        console.log('[socket] connected', socket.id);
      }
      setIsConnected(true);
    });

    socket.on('disconnect', (reason: string) => {
      if (showLogger) {
        // eslint-disable-next-line no-console
        console.warn('[socket] disconnected', { reason });
      }
    });

    socket.io.on('reconnect_attempt', (attempt: number) => {
      if (showLogger) {
        // eslint-disable-next-line no-console
        console.info('[socket] reconnect_attempt', { attempt });
      }
    });

    socket.io.on('reconnect_error', (err: unknown) => {
      if (showLogger) {
        // eslint-disable-next-line no-console
        console.error('[socket] reconnect_error', err);
      }
    });

    socket.io.on('reconnect_failed', () => {
      if (showLogger) {
        // eslint-disable-next-line no-console
        console.error('[socket] reconnect_failed');
      }
    });

    socket.on('connect_error', (err: unknown) => {
      if (showLogger) {
        // eslint-disable-next-line no-console
        console.error('[socket] connect_error', err);
        // Helpful hint for browser limitations
        // eslint-disable-next-line no-console
        console.log(
          '[socket] If Unauthorized in browser: server must read credentials from socket.handshake.auth or query (headers are not sent by browsers during WS handshake).'
        );
      }
    });

    socket.on('error', (err: unknown) => {
      if (showLogger) {
        // eslint-disable-next-line no-console
        console.error('[socket] error', err);
      }
    });

    socket.on('create', (payload: unknown) => {
      if (showLogger) {
        // eslint-disable-next-line no-console
        console.log('[socket] create event', payload);
      }
      setEventId(payload); // trigger re-render
    });

    socketRef.current = socket;
    if (showLogger) {
      // Expose for console debugging
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__faceIdsSocket = socket;
    }

    return () => {
      try {
        socket.off('create');
        socket.off('connect');
        socket.off('disconnect');
        socket.off('connect_error');
        socket.off('error');
        socket.io.off('reconnect_attempt');
        socket.io.off('reconnect_error');
        socket.io.off('reconnect_failed');
        socket.disconnect();
        // eslint-disable-next-line no-console
        console.log('[socket] disconnected');
      } catch (_) {
        // ignore
      }
      socketRef.current = null;
      if (showLogger) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (window as any).__faceIdsSocket;
      }
    };
  }, [url, reconnectTrigger]);
  return (
    <ClusterDbSocketContext.Provider
      value={{
        socket: socketRef.current,
        eventId,
        reconnect,
        isConnected,
      }}
    >
      {children}
    </ClusterDbSocketContext.Provider>
  );
}

export default useClusterDbSocket;
